import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { EnvironmentProviders, inject, Injectable, makeEnvironmentProviders } from "@angular/core";

import { firstValueFrom } from "rxjs";

import {
  AssistantActionBody,
  AssistantAssignmentsQuery,
  AssistantAssignmentsResponse
} from "../../models/chat/assistant-contracts";
import {
  ChatCapabilitiesResponse,
  ChatContextRequest,
  ChatConversationPageResponse,
  ChatConversationResponse,
  ChatHistoryResponse,
  ChatSendBody,
  ChatStreamEvent,
  ChatTurnResponse
} from "../../models/chat/chat-contracts";
import { SUPPRESS_FORBIDDEN_REDIRECT } from "../../interceptors/custon-http.interceptor";
import { LektoAuthService } from "../essential/lekto-auth.service";
import { LibraryInterface } from "../../models/core/library.interface";
import { PaginatedResponse } from "../../models/paginated-response.interface";

import {
  ASSISTANT_CONTENT_ENDPOINT,
  ASSISTANT_ENDPOINT,
  ASSISTANT_TRANSPORT,
  AssistantTransport
} from "./assistant-transport";
import { AssistantHttpError, ChatStreamService } from "./chat-stream.service";

@Injectable()
export class ChatApiService implements AssistantTransport {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(LektoAuthService);
  private readonly streamService = inject(ChatStreamService);
  private readonly endpoint = inject(ASSISTANT_ENDPOINT).replace(/\/$/, "");
  private readonly contentEndpoint = inject(ASSISTANT_CONTENT_ENDPOINT).replace(/\/$/, "");

  public capabilities(): Promise<ChatCapabilitiesResponse> {
    return firstValueFrom(
      this.http.get<ChatCapabilitiesResponse>(`${this.endpoint}/capabilities`, {
        context: this.localErrorContext()
      })
    );
  }

  public recent(
    workflowKey: string,
    limit = 1,
    cursor?: string
  ): Promise<ChatConversationPageResponse> {
    let params = new HttpParams().set("workflow", workflowKey).set("limit", limit);
    if (cursor) params = params.set("cursor", cursor);
    return firstValueFrom(
      this.http.get<ChatConversationPageResponse>(`${this.endpoint}/conversations`, {
        params,
        context: this.localErrorContext()
      })
    );
  }

  public create(context?: ChatContextRequest | null): Promise<ChatConversationResponse> {
    return firstValueFrom(
      this.http.post<ChatConversationResponse>(
        `${this.endpoint}/conversations`,
        { context: context ?? null },
        { context: this.localErrorContext() }
      )
    );
  }

  public history(
    conversationId: string,
    cursor?: string,
    pageSize = 100
  ): Promise<ChatHistoryResponse> {
    let params = new HttpParams().set("pageSize", pageSize);
    if (cursor) params = params.set("cursor", cursor);
    return firstValueFrom(
      this.http.get<ChatHistoryResponse>(
        `${this.endpoint}/conversations/${encodeURIComponent(conversationId)}/history`,
        { params, context: this.localErrorContext() }
      )
    );
  }

  public send(
    conversationId: string,
    body: ChatSendBody,
    signal: AbortSignal
  ): AsyncIterable<ChatStreamEvent> {
    const token = this.auth.getTokenData();
    if (!token?.accessToken) throw new AssistantHttpError(401);
    const headers: Record<string, string> = {
      Authorization: `${token.tokenType} ${token.accessToken}`
    };
    try {
      const profile = this.auth.getProfileData();
      if (profile?.coRole) headers["X-Current-Role"] = profile.coRole;
      if (profile?.idNetwork) headers["X-Current-Network"] = profile.idNetwork;
    } catch {
      throw new AssistantHttpError(401);
    }
    return this.streamService.stream(
      `${this.endpoint}/conversations/${encodeURIComponent(conversationId)}/messages`,
      headers,
      body,
      signal
    );
  }

  public async remove(conversationId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(
        `${this.endpoint}/conversations/${encodeURIComponent(conversationId)}`,
        { context: this.localErrorContext() }
      )
    );
  }

  public action(conversationId: string, body: AssistantActionBody): Promise<ChatTurnResponse> {
    return firstValueFrom(
      this.http.post<ChatTurnResponse>(
        `${this.endpoint}/conversations/${encodeURIComponent(conversationId)}/actions`,
        body,
        { context: this.localErrorContext() }
      )
    );
  }

  public assignments(query: AssistantAssignmentsQuery): Promise<AssistantAssignmentsResponse> {
    let params = new HttpParams();
    if (query.idSchool) params = params.set("idSchool", query.idSchool);
    if (query.idScreenAssignment)
      params = params.set("idScreenAssignment", query.idScreenAssignment);
    return firstValueFrom(
      this.http.get<AssistantAssignmentsResponse>(`${this.endpoint}/assistant/assignments`, {
        params,
        context: this.localErrorContext()
      })
    );
  }

  public async contentCards(ids: readonly string[]): Promise<readonly LibraryInterface[]> {
    let params = new HttpParams().set("page", 1).set("pageSize", ids.length);
    for (const id of ids) params = params.append("idContentTemplates", id);
    const page = await firstValueFrom(
      this.http.get<PaginatedResponse<LibraryInterface>>(`${this.contentEndpoint}/by-user`, {
        params,
        context: this.localErrorContext()
      })
    );
    return page.content ?? [];
  }

  private localErrorContext(): HttpContext {
    return new HttpContext().set(SUPPRESS_FORBIDDEN_REDIRECT, true);
  }
}

export function provideAssistantTransport(
  endpoint: string,
  contentEndpoint: string
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ASSISTANT_ENDPOINT, useValue: endpoint },
    { provide: ASSISTANT_CONTENT_ENDPOINT, useValue: contentEndpoint },
    ChatApiService,
    { provide: ASSISTANT_TRANSPORT, useExisting: ChatApiService }
  ]);
}
